// src/utils/sendOrderEmails.js
import emailjs from "@emailjs/browser";


const SERVICE_ID = process.env.REACT_APP_EMAILJS_ORDER_SERVICE_ID?.trim() || "";
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID?.trim() || "";
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY?.trim() || "";

// const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID?.trim() || "";
// const CUSTOMER_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_CUSTOMER_TEMPLATE_ID?.trim() || "";
// const ADMIN_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_ADMIN_TEMPLATE_ID?.trim() || "";
// const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY?.trim() || "";
// const ADMIN_EMAIL = process.env.REACT_APP_EMAILJS_ADMIN_EMAIL?.trim() || "";

// ─── Shared Helpers ────────────────────────────────────────────────────────────

function formatCurrency(amount) {
  return `Rs. ${Number(amount).toLocaleString("en-PK")}`;
}

function formatPaymentMethod(method) {
  return method === "bank" ? "Bank Deposit" : "Cash on Delivery";
}

function buildItemRows(items) {
  return items
    .map(
      (item) => `
      <tr>
        <td style="padding:12px 16px;border-bottom:1px solid #f0f0f0;vertical-align:middle;width:60px;">
          <img src="${item.picture}" alt="${item.name}"
            style="width:52px;height:52px;object-fit:cover;border-radius:8px;border:1px solid #eee;display:block;" />
        </td>
        <td style="padding:12px 16px;border-bottom:1px solid #f0f0f0;vertical-align:middle;">
          <div style="font-weight:600;color:#1a1a1a;font-size:14px;margin-bottom:2px;">${item.name}</div>
          ${item.manufactureredBy ? `<div style="font-size:12px;color:#888;">by ${item.manufactureredBy}</div>` : ""}
        </td>
        <td style="padding:12px 16px;border-bottom:1px solid #f0f0f0;text-align:center;color:#555;font-size:14px;vertical-align:middle;">
          ${item.quantity}
        </td>
        <td style="padding:12px 16px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:600;color:#1a1a1a;font-size:14px;vertical-align:middle;">
          ${formatCurrency(item.price)}
        </td>
        <td style="padding:12px 16px;border-bottom:1px solid #f0f0f0;text-align:right;font-weight:700;color:#d2222d;font-size:14px;vertical-align:middle;">
          ${formatCurrency(item.price * item.quantity)}
        </td>
      </tr>`
    )
    .join("");
}

// ─── Customer Email HTML ────────────────────────────────────────────────────────

function buildLogoBlock(logoURL) {
  if (!logoURL || !logoURL.startsWith("http")) return "";
  return `<img src="${logoURL}" alt="Dawaa Pharmacy Logo"
    style="max-height:56px;max-width:180px;object-fit:contain;display:block;margin:0 auto 10px;" />`;
}

function buildCustomerHTML(order, contact = {}) {
  const { customerDetails: cd, items, subtotal, shipping, total } = order;
  const phoneNumber = contact.phoneNumber || "+06323839204";
  const whatsappNumber = (contact.whatsappNumber || "+06378010848").replace(/\D/g, "");
  const logoBlock = buildLogoBlock(contact.footerLogoURL);
  const fullName = `${cd.firstName} ${cd.lastName}`;
  const fullAddress = [cd.street, cd.address2, cd.city, cd.region, cd.postalCode, cd.country]
    .filter(Boolean)
    .join(", ");
  const isBankDeposit = cd.paymentMethod === "bank";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1.0" />
  <title>Order Confirmation – Dawaa Pharmacy</title>
  <style>
    @media only screen and (max-width:480px) {
      .col-half { display:block !important; width:100% !important; padding-left:0 !important; padding-right:0 !important; box-sizing:border-box; }
      .col-half + .col-half { padding-top:10px !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;background-color:#f5f5f5;font-family:Arial,Helvetica,sans-serif;-webkit-font-smoothing:antialiased;">

  <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background:#f5f5f5;padding:32px 16px;">
    <tr>
      <td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" role="presentation"
          style="max-width:620px;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">

          <!-- ── Header ── -->
          <tr>
            <td style="background-color:#d2222d;padding:36px 40px;text-align:center;">
              ${logoBlock}
              <h1 style="margin:0;color:#ffffff;font-size:28px;font-weight:800;letter-spacing:-0.5px;"><span style="color:#ffcdd2;">PHARMACY</span></h1>
              <p style="margin:6px 0 0;color:rgba(255,255,255,0.85);font-size:13px;letter-spacing:0.5px;">Your Trusted Online Pharmacy</p>
            </td>
          </tr>

          <!-- ── Hero Banner ── -->
          <tr>
            <td style="background:#fff8f8;border-bottom:3px solid #d2222d;padding:28px 40px;text-align:center;">
              <div style="display:inline-block;border-radius:50%;width:56px;height:56px;line-height:56px;text-align:center;margin-bottom:14px;">
                <span style="font-size:26px;">✅</span>
              </div>
              <h2 style="margin:0 0 8px;color:#1a1a1a;font-size:22px;font-weight:700;">Order Confirmed!</h2>
              <p style="margin:0;color:#666;font-size:15px;line-height:1.6;">
                Hi <strong style="color:#d2222d;">${fullName}</strong>, thank you for your order.<br />
                We've received it and will process it shortly.
              </p>
            </td>
          </tr>

          <!-- ── Body ── -->
          <tr>
            <td style="padding:32px 40px;">

              <!-- Delivery & Payment Info -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:28px;">
                <tr>
                  <!-- Delivery -->
                  <td class="col-half" width="50%" style="vertical-align:top;padding-right:10px;">
                    <div style="background:#fafafa;border:1px solid #eeeeee;border-radius:12px;padding:18px;">
                      <div style="font-size:11px;font-weight:700;color:#d2222d;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">📦 Delivery Address</div>
                      <div style="font-size:13px;color:#444;line-height:1.7;">${fullAddress}</div>
                      ${cd.orderNotes ? `<div style="margin-top:10px;font-size:12px;color:#888;font-style:italic;">Note: ${cd.orderNotes}</div>` : ""}
                    </div>
                  </td>
                  <!-- Payment -->
                  <td class="col-half" width="50%" style="vertical-align:top;padding-left:10px;">
                    <div style="background:#fafafa;border:1px solid #eeeeee;border-radius:12px;padding:18px;">
                      <div style="font-size:11px;font-weight:700;color:#d2222d;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">💳 Payment Method</div>
                      <div style="font-size:13px;color:#444;font-weight:600;">${formatPaymentMethod(cd.paymentMethod)}</div>
                      <div style="margin-top:8px;font-size:12px;color:#888;">Phone: ${cd.phone}</div>
                      <div style="font-size:12px;color:#888;">Email: ${cd.email}</div>
                    </div>
                  </td>
                </tr>
              </table>

              ${isBankDeposit ? `
              <!-- Bank Deposit Notice -->
              <div style="background:#fff3cd;border:1px solid #ffc107;border-radius:12px;padding:18px 20px;margin-bottom:28px;">
                <div style="font-size:13px;font-weight:700;color:#856404;margin-bottom:6px;">⚠️ Bank Deposit Instructions</div>
                <div style="font-size:13px;color:#666;line-height:1.7;">
                  Please transfer <strong style="color:#d2222d;">${formatCurrency(total)}</strong> to the bank account provided and send the payment screenshot on WhatsApp to confirm your order.
                </div>
              </div>` : ""}

              <!-- Order Items -->
              <div style="margin-bottom:28px;">
                <h3 style="margin:0 0 16px;font-size:16px;color:#1a1a1a;font-weight:700;border-bottom:2px solid #d2222d;padding-bottom:8px;">🛒 Your Order Items</h3>
                <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #f0f0f0;border-radius:12px;overflow:hidden;">
                  <thead>
                    <tr style="background:#fafafa;">
                      <th style="padding:12px 16px;text-align:center;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;width:60px;">Image</th>
                      <th style="padding:12px 16px;text-align:left;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Product</th>
                      <th style="padding:12px 16px;text-align:center;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Qty</th>
                      <th style="padding:12px 16px;text-align:right;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Price</th>
                      <th style="padding:12px 16px;text-align:right;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${buildItemRows(items)}
                  </tbody>
                </table>
              </div>

              <!-- Order Summary -->
              <table width="100%" cellpadding="0" cellspacing="0"
                style="background:#fafafa;border:1px solid #eee;border-radius:12px;overflow:hidden;margin-bottom:28px;">
                <tr>
                  <td style="padding:14px 20px;font-size:14px;color:#555;border-bottom:1px solid #eee;">Subtotal</td>
                  <td style="padding:14px 20px;font-size:14px;color:#1a1a1a;font-weight:600;text-align:right;border-bottom:1px solid #eee;">${formatCurrency(subtotal)}</td>
                </tr>
                <tr>
                  <td style="padding:14px 20px;font-size:14px;color:#555;border-bottom:1px solid #eee;">Shipping</td>
                  <td style="padding:14px 20px;font-size:14px;color:#1a1a1a;font-weight:600;text-align:right;border-bottom:1px solid #eee;">${formatCurrency(shipping)}</td>
                </tr>
                <tr style="background:#d2222d;">
                  <td style="padding:16px 20px;font-size:16px;color:#ffffff;font-weight:700;">Grand Total</td>
                  <td style="padding:16px 20px;font-size:18px;color:#ffffff;font-weight:800;text-align:right;">${formatCurrency(total)}</td>
                </tr>
              </table>

              <!-- Contact Us -->
              <div style="background:#fafafa;border:1px solid #eee;border-radius:12px;padding:20px;text-align:center;">
                <p style="margin:0 0 12px;font-size:14px;color:#555;">Questions about your order? We're here to help.</p>
                <a href="tel:${phoneNumber}" style="display:inline-block;background:#d2222d;color:#ffffff;text-decoration:none;padding:10px 22px;border-radius:8px;font-size:13px;font-weight:600;margin:4px;">📞 Call Us</a>
                <a href="https://wa.me/${whatsappNumber}" style="display:inline-block;background:#25d366;color:#ffffff;text-decoration:none;padding:10px 22px;border-radius:8px;font-size:13px;font-weight:600;margin:4px;">💬 WhatsApp</a>
              </div>

            </td>
          </tr>

          <!-- ── Footer ── -->
          <tr>
            <td style="background:#1a1a1a;padding:24px 40px;text-align:center;">
              <p style="margin:0 0 6px;color:#ffffff;font-size:15px;font-weight:700;">DAWAA PHARMACY</p>
              <p style="margin:0 0 10px;color:#aaa;font-size:12px;">Find us right opposite Ikram Hospital, Bhimber road Gujrat.</p>
              <p style="margin:0;color:#666;font-size:11px;">© ${new Date().getFullYear()} Dawaa Pharmacy. All rights reserved.</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>

</body>
</html>`;
}

// ─── Admin Email HTML ───────────────────────────────────────────────────────────

function buildAdminHTML(order, contact = {}) {
  const { customerDetails: cd, items, subtotal, shipping, total } = order;
  const logoBlock = buildLogoBlock(contact.footerLogoURL);
  const fullName = `${cd.firstName} ${cd.lastName}`;
  const fullAddress = [cd.street, cd.address2, cd.city, cd.region, cd.postalCode, cd.country]
    .filter(Boolean)
    .join(", ");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1.0" />
  <title>New Order Alert – Dawaa Pharmacy</title>
  <style>
    @media only screen and (max-width:480px) {
      .col-half { display:block !important; width:100% !important; padding-left:0 !important; padding-right:0 !important; box-sizing:border-box; }
      .col-half + .col-half { padding-top:10px !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;background-color:#f0f0f0;font-family:Arial,Helvetica,sans-serif;-webkit-font-smoothing:antialiased;">

  <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background:#f0f0f0;padding:32px 16px;">
    <tr>
      <td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" role="presentation"
          style="max-width:640px;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.10);">

          <!-- ── Admin Header ── -->
          
          <!-- ── Alert Banner ── -->
          <tr>
            <td style="background-color:#d2222d;padding:22px 40px;text-align:center;">
              ${logoBlock}
              <p style="margin:0;color:#ffffff;font-size:18px;font-weight:700;letter-spacing:0.3px;">
                🔔 New Order Received!
              </p>
              <p style="margin:6px 0 0;color:rgba(255,255,255,0.8);font-size:13px;">
                A customer just placed an order. Review and process it promptly.
              </p>
            </td>
          </tr>

          <!-- ── Body ── -->
          <tr>
            <td style="padding:32px 40px;">

              <!-- Customer Info Grid -->
              <h3 style="margin:0 0 16px;font-size:15px;color:#1a1a1a;font-weight:700;border-bottom:2px solid #d2222d;padding-bottom:8px;">
                👤 Customer Information
              </h3>
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:28px;">
                <tr>
                  <td class="col-half" width="50%" style="vertical-align:top;padding-right:10px;">
                    <div style="background:#fafafa;border:1px solid #eee;border-radius:12px;padding:16px;">
                      <div style="font-size:11px;font-weight:700;color:#d2222d;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">Contact</div>
                      <div style="font-size:14px;color:#1a1a1a;font-weight:600;margin-bottom:4px;">${fullName}</div>
                      <div style="font-size:13px;color:#555;margin-bottom:2px;">📞 ${cd.phone}</div>
                      <div style="font-size:13px;color:#555;">✉️ ${cd.email}</div>
                    </div>
                  </td>
                  <td class="col-half" width="50%" style="vertical-align:top;padding-left:10px;">
                    <div style="background:#fafafa;border:1px solid #eee;border-radius:12px;padding:16px;">
                      <div style="font-size:11px;font-weight:700;color:#d2222d;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">Delivery</div>
                      <div style="font-size:13px;color:#444;line-height:1.7;">${fullAddress}</div>
                    </div>
                  </td>
                </tr>
              </table>

              <!-- Payment Info -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:28px;">
                <tr>
                  <td class="col-half" width="50%" style="vertical-align:top;padding-right:10px;">
                    <div style="background:#fafafa;border:1px solid #eee;border-radius:12px;padding:16px;">
                      <div style="font-size:11px;font-weight:700;color:#d2222d;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">💳 Payment Method</div>
                      <div style="font-size:14px;color:#1a1a1a;font-weight:600;">${formatPaymentMethod(cd.paymentMethod)}</div>
                    </div>
                  </td>
                  <td class="col-half" width="50%" style="vertical-align:top;padding-left:10px;">
                    <div style="background:#fafafa;border:1px solid #eee;border-radius:12px;padding:16px;">
                      <div style="font-size:11px;font-weight:700;color:#d2222d;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">📋 Status</div>
                      <div style="display:inline-block;background:#fff3f3;border:1.5px solid #d2222d;color:#d2222d;border-radius:100px;padding:4px 14px;font-size:13px;font-weight:700;">
                        Pending
                      </div>
                    </div>
                  </td>
                </tr>
              </table>

              ${cd.orderNotes ? `
              <div style="background:#fff8e1;border:1px solid #ffe082;border-radius:12px;padding:16px 20px;margin-bottom:28px;">
                <div style="font-size:11px;font-weight:700;color:#f57f17;text-transform:uppercase;letter-spacing:1px;margin-bottom:6px;">📝 Order Notes</div>
                <div style="font-size:13px;color:#555;">${cd.orderNotes}</div>
              </div>` : ""}

              <!-- Order Items -->
              <h3 style="margin:0 0 16px;font-size:15px;color:#1a1a1a;font-weight:700;border-bottom:2px solid #d2222d;padding-bottom:8px;">
                🛒 Ordered Items (${items.length} item${items.length > 1 ? "s" : ""})
              </h3>
              <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #f0f0f0;border-radius:12px;overflow:hidden;margin-bottom:28px;">
                <thead>
                  <tr style="background:#fafafa;">
                    <th style="padding:12px 16px;text-align:center;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;width:60px;">Image</th>
                    <th style="padding:12px 16px;text-align:left;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Product</th>
                    <th style="padding:12px 16px;text-align:center;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Qty</th>
                    <th style="padding:12px 16px;text-align:right;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Price</th>
                    <th style="padding:12px 16px;text-align:right;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Total</th>
                  </tr>
                </thead>
                <tbody>
                  ${buildItemRows(items)}
                </tbody>
              </table>

              <!-- Order Summary -->
              <table width="100%" cellpadding="0" cellspacing="0"
                style="background:#fafafa;border:1px solid #eee;border-radius:12px;overflow:hidden;">
                <tr>
                  <td style="padding:14px 20px;font-size:14px;color:#555;border-bottom:1px solid #eee;">Subtotal</td>
                  <td style="padding:14px 20px;font-size:14px;color:#1a1a1a;font-weight:600;text-align:right;border-bottom:1px solid #eee;">${formatCurrency(subtotal)}</td>
                </tr>
                <tr>
                  <td style="padding:14px 20px;font-size:14px;color:#555;border-bottom:1px solid #eee;">Shipping</td>
                  <td style="padding:14px 20px;font-size:14px;color:#1a1a1a;font-weight:600;text-align:right;border-bottom:1px solid #eee;">${formatCurrency(shipping)}</td>
                </tr>
                <tr style="background:#d2222d;">
                  <td style="padding:16px 20px;font-size:16px;color:#ffffff;font-weight:700;">Grand Total</td>
                  <td style="padding:16px 20px;font-size:18px;color:#ffffff;font-weight:800;text-align:right;">${formatCurrency(total)}</td>
                </tr>
              </table>

            </td>
          </tr>

          <!-- ── Footer ── -->
          <tr>
            <td style="background:#1a1a1a;padding:20px 40px;text-align:center;">
              <p style="margin:0 0 4px;color:#aaa;font-size:12px;">This is an automated alert from Dawaa Pharmacy Admin System.</p>
              <p style="margin:0;color:#666;font-size:11px;">© ${new Date().getFullYear()} Dawaa Pharmacy. All rights reserved.</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>

</body>
</html>`;
}

// ─── Status Color Helper ────────────────────────────────────────────────────────

function statusStyle(status) {
  const map = {
    Pending: { bg: "#fff3cd", color: "#856404", border: "#ffc107" },
    Processing: { bg: "#fff3cd", color: "#856404", border: "#ffc107" },
    Shipped: { bg: "#e8f4fd", color: "#0c5f9e", border: "#90caf9" },
    Delivered: { bg: "#e6f9ee", color: "#1a7a40", border: "#81c784" },
    Cancelled: { bg: "#f9e0e4", color: "#c90f2f", border: "#ef9a9a" },
  };
  return map[status] || { bg: "#f5f5f5", color: "#555", border: "#ddd" };
}

// ─── Status Update Email HTML ───────────────────────────────────────────────────

function buildStatusUpdateHTML(order, newStatus, trackingId, contact = {}) {
  const { customerDetails: cd, items, subtotal, shipping, total } = order;
  const phoneNumber = contact.phoneNumber || "+06323839204";
  const whatsappNumber = (contact.whatsappNumber || "+06378010848").replace(/\D/g, "");
  const logoBlock = buildLogoBlock(contact.footerLogoURL);
  const fullName = `${cd.firstName} ${cd.lastName}`;
  const sStyle = statusStyle(newStatus);

  const statusEmoji = {
    Pending: "⏳",
    Processing: "🔄",
    Shipped: "🚚",
    Delivered: "✅",
    Cancelled: "❌",
  }[newStatus] || "📦";

  const trackingBlock = trackingId
    ? `<tr>
        <td colspan="5" style="padding:14px 16px;background:#f0f7ff;border-top:2px solid #90caf9;">
          <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
            <span style="font-size:18px;">📦</span>
            <div>
              <div style="font-size:11px;font-weight:700;color:#0c5f9e;text-transform:uppercase;letter-spacing:1px;margin-bottom:2px;">Tracking ID</div>
              <div style="font-size:15px;font-weight:700;color:#1a1a1a;font-family:monospace;letter-spacing:0.5px;">${trackingId}</div>
            </div>
          </div>
        </td>
      </tr>`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1.0" />
  <title>Order Status Update – Dawaa Pharmacy</title>
  <style>
    @media only screen and (max-width:480px) {
      .col-half { display:block !important; width:100% !important; padding-left:0 !important; padding-right:0 !important; box-sizing:border-box; }
      .col-half + .col-half { padding-top:10px !important; }
    }
  </style>
</head>
<body style="margin:0;padding:0;background-color:#f5f5f5;font-family:Arial,Helvetica,sans-serif;-webkit-font-smoothing:antialiased;">

  <table width="100%" cellpadding="0" cellspacing="0" role="presentation" style="background:#f5f5f5;padding:32px 16px;">
    <tr>
      <td align="center">
        <table width="100%" cellpadding="0" cellspacing="0" role="presentation"
          style="max-width:620px;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);">

          <!-- Header -->
          <tr>
            <td style="background-color:#d2222d;padding:36px 40px;text-align:center;">
              ${logoBlock}
              <h1 style="margin:0;color:#ffffff;font-size:28px;font-weight:800;letter-spacing:-0.5px;"><span style="color:#ffcdd2;">PHARMACY</span></h1>
              <p style="margin:6px 0 0;color:rgba(255,255,255,0.85);font-size:13px;letter-spacing:0.5px;">Your Trusted Online Pharmacy</p>
            </td>
          </tr>

          <!-- Status Banner -->
          <tr>
            <td style="background:#fff8f8;border-bottom:3px solid #d2222d;padding:28px 40px;text-align:center;">
              <div style="display:inline-block;margin-bottom:14px;font-size:32px;">${statusEmoji}</div>
              <h2 style="margin:0 0 10px;color:#1a1a1a;font-size:22px;font-weight:700;">Order Status Updated</h2>
              <p style="margin:0 0 16px;color:#666;font-size:15px;line-height:1.6;">
                Hi <strong style="color:#d2222d;">${fullName}</strong>, your order status has been updated.
              </p>
              <div style="display:inline-block;padding:10px 28px;border-radius:100px;font-size:16px;font-weight:700;letter-spacing:0.5px;
                background:${sStyle.bg};color:${sStyle.color};border:2px solid ${sStyle.border};">
                ${statusEmoji} ${newStatus}
              </div>
            </td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding:32px 40px;">

              <!-- Order Items -->
              <div style="margin-bottom:28px;">
                <h3 style="margin:0 0 16px;font-size:16px;color:#1a1a1a;font-weight:700;border-bottom:2px solid #d2222d;padding-bottom:8px;">🛒 Your Order Items</h3>
                <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #f0f0f0;border-radius:12px;overflow:hidden;">
                  <thead>
                    <tr style="background:#fafafa;">
                      <th style="padding:12px 16px;text-align:center;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;width:60px;">Image</th>
                      <th style="padding:12px 16px;text-align:left;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Product</th>
                      <th style="padding:12px 16px;text-align:center;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Qty</th>
                      <th style="padding:12px 16px;text-align:right;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Price</th>
                      <th style="padding:12px 16px;text-align:right;font-size:12px;font-weight:700;color:#888;text-transform:uppercase;letter-spacing:0.5px;">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${buildItemRows(items)}
                    ${trackingBlock}
                  </tbody>
                </table>
              </div>

              <!-- Order Summary -->
              <table width="100%" cellpadding="0" cellspacing="0"
                style="background:#fafafa;border:1px solid #eee;border-radius:12px;overflow:hidden;margin-bottom:28px;">
                <tr>
                  <td style="padding:14px 20px;font-size:14px;color:#555;border-bottom:1px solid #eee;">Subtotal</td>
                  <td style="padding:14px 20px;font-size:14px;color:#1a1a1a;font-weight:600;text-align:right;border-bottom:1px solid #eee;">${formatCurrency(subtotal)}</td>
                </tr>
                <tr>
                  <td style="padding:14px 20px;font-size:14px;color:#555;border-bottom:1px solid #eee;">Shipping</td>
                  <td style="padding:14px 20px;font-size:14px;color:#1a1a1a;font-weight:600;text-align:right;border-bottom:1px solid #eee;">${formatCurrency(shipping)}</td>
                </tr>
                <tr style="background:#d2222d;">
                  <td style="padding:16px 20px;font-size:16px;color:#ffffff;font-weight:700;">Grand Total</td>
                  <td style="padding:16px 20px;font-size:18px;color:#ffffff;font-weight:800;text-align:right;">${formatCurrency(total)}</td>
                </tr>
              </table>

              <!-- Contact Us -->
              <div style="background:#fafafa;border:1px solid #eee;border-radius:12px;padding:20px;text-align:center;">
                <p style="margin:0 0 12px;font-size:14px;color:#555;">Questions about your order? We're here to help.</p>
                <a href="tel:${phoneNumber}" style="display:inline-block;background:#d2222d;color:#ffffff;text-decoration:none;padding:10px 22px;border-radius:8px;font-size:13px;font-weight:600;margin:4px;">📞 Call Us</a>
                <a href="https://wa.me/${whatsappNumber}" style="display:inline-block;background:#25d366;color:#ffffff;text-decoration:none;padding:10px 22px;border-radius:8px;font-size:13px;font-weight:600;margin:4px;">💬 WhatsApp</a>
              </div>

            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="background:#1a1a1a;padding:24px 40px;text-align:center;">
              <p style="margin:0 0 6px;color:#ffffff;font-size:15px;font-weight:700;">DAWAA PHARMACY</p>
              <p style="margin:0 0 10px;color:#aaa;font-size:12px;">Find us right opposite Ikram Hospital, Bhimber road Gujrat.</p>
              <p style="margin:0;color:#666;font-size:11px;">© ${new Date().getFullYear()} Dawaa Pharmacy. All rights reserved.</p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>

</body>
</html>`;
}

// ─── Main Exports ────────────────────────────────────────────────────────────────

/**
 * Sends two emails after a successful order:
 *  1. Confirmation to the customer
 *  2. New-order alert to the admin
 *
 * @param {object} order - Full order object saved to Firestore
 * @param {string} adminEmail - Admin email address to notify
 */
export async function sendOrderEmails(order, adminEmail, contact = {}) {
  const customerHTML = buildCustomerHTML(order, contact);
  const adminHTML = buildAdminHTML(order, contact);

  const customerParams = {
    title: "✅ Your Dawaa Pharmacy Order is Confirmed!",
    name: `Dawaa Pharmacy`,
    message: customerHTML,
    email: order.customerDetails.email,
    reply_to: 'info@dawaacare.com',
  };

  const adminParams = {
    title: `🔔 New Order from ${order.customerDetails.firstName} ${order.customerDetails.lastName} — ${formatCurrency(order.total)}`,
    name: "Dawaa Pharmacy",
    message: adminHTML,
    email: 'info@dawaacare.com',
    reply_to: order.customerDetails.email,
  };

  await Promise.all([
    emailjs.send(SERVICE_ID, TEMPLATE_ID, customerParams, PUBLIC_KEY),
    emailjs.send(SERVICE_ID, TEMPLATE_ID, adminParams, PUBLIC_KEY),
  ]);
}

/**
 * Sends a status-update email to the customer when admin changes order status.
 *
 * @param {object} order         - Full order object from Firestore
 * @param {string} newStatus     - The new status string (e.g. "Shipped")
 * @param {string} trackingId    - Optional tracking ID to include in the email
 * @param {object} contact       - Optional contact/branding overrides
 */
export async function sendStatusUpdateEmail(order, newStatus, trackingId = "", contact = {}) {
  const html = buildStatusUpdateHTML(order, newStatus, trackingId, contact);
  // const fullName = `${order.customerDetails.firstName} ${order.customerDetails.lastName}`;

  const params = {
    title: `📦 Your Dawaa Pharmacy Order is now "${newStatus}"`,
    name: "Dawaa Pharmacy",
    message: html,
    email: order.customerDetails.email,
    reply_to: 'info@dawaacare.com',
  };

  await emailjs.send(SERVICE_ID, TEMPLATE_ID, params, PUBLIC_KEY);
}
